import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Activity, Filter, AlertTriangle, Truck, Fuel, Camera, FileText, Wallet, Wrench, ClipboardCheck, FileBadge, MessageSquare, CircleDollarSign, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Row {
  id: string;
  source: 'system' | 'user';
  occurredAt: string;
  entityType: string;
  description: string;
  userName: string | null;
  siteId: string | null;
  siteName: string | null;
  isLate: boolean;
  daysLate: number;
}

const ICON_FOR: Record<string, React.ElementType> = {
  trip: Truck, fuel: Fuel, photo: Camera, voucher: FileText, expense: Wallet,
  cash: CircleDollarSign, maintenance: Wrench, attendance: UserCheck,
  document: FileBadge, coaching: MessageSquare, anomaly: AlertTriangle,
  closing: ClipboardCheck,
};

function fmtTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function ActivityFeedPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [entityType, setEntityType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to)   params.set('to', to);
      if (entityType) params.set('entityType', entityType);
      params.set('limit', '500');
      const r = await apiClient.get<Row[]>('/api/v1/activity/range?' + params.toString());
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, entityType]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Activity className="h-4 w-4" /> Activity feed</div>
          <Button variant="ghost" size="sm" onClick={() => setShowFilters((s) => !s)} className="gap-2 h-8"><Filter className="h-3.5 w-3.5" /> Filter</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        {showFilters && (
          <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label htmlFor="from">From</Label><Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" /></div>
            <div className="space-y-1"><Label htmlFor="to">To</Label><Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" /></div>
            <div className="space-y-1">
              <Label htmlFor="et">Type</Label>
              <select id="et" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="flex h-11 w-44 rounded-md border border-input bg-background px-3 text-base">
                <option value="">All</option>
                {['trip', 'fuel', 'photo', 'expense', 'closing', 'maintenance', 'anomaly', 'document'].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No activity in this range.</div>
        ) : (
          <div className="space-y-1">
            {rows.map((r) => {
              const Icon = ICON_FOR[r.entityType] ?? Activity;
              return (
                <div key={r.id} className="rounded-lg border border-border p-3 flex items-baseline gap-3 text-sm">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className="font-mono text-xs text-muted-foreground">{fmtTime(r.occurredAt)}</span>
                      {r.siteName && <span className="text-xs text-muted-foreground">{r.siteName}</span>}
                    </div>
                    <div className="mt-0.5">{r.description}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {r.source === 'user' ? '👤 ' : ''}{r.userName ?? ''}{r.daysLate > 0 && <span className="text-amber-600 dark:text-amber-400 ms-2">added {r.daysLate} day{r.daysLate > 1 ? 's' : ''} late</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
